/**
 * HTML sanitization utilities to prevent XSS attacks
 *
 * SECURITY: Always use escapeHtml() when inserting user-provided or
 * external data into innerHTML, SVG templates, or any HTML context.
 */

/**
 * Escapes HTML special characters to prevent XSS when inserting
 * user-controlled data into HTML/SVG templates.
 *
 * @param unsafe - The untrusted string to escape
 * @returns The escaped string safe for HTML insertion
 *
 * @example
 * // In SVG template
 * container.innerHTML = `<text>${escapeHtml(unitNumber)}</text>`;
 *
 * // In HTML template
 * toast.innerHTML = `<span>${escapeHtml(message)}</span>`;
 */
export function escapeHtml(unsafe: string | null | undefined): string {
  if (unsafe == null) return '';

  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Sanitizes a string for use in SVG text elements.
 * Same as escapeHtml but explicitly named for clarity.
 */
export const sanitizeForSvg = escapeHtml;

/**
 * Validates that a value is a safe number for use in SVG attributes.
 * Returns 0 if the value is not a finite number.
 */
export function sanitizeNumber(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}
